import React, { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import Table from '../../../../components/Table';
import StatusPill, { PillTone } from '../../../../components/StatusPill';
import {
  Employee,
  getEmployeeName,
  getEmployeeStatus,
  formatDate
} from '../../config';

interface EmployeeTableProps {
  employees: Employee[];
  onRowClick: (employee: Employee) => void;
}

// The three enums in the pill's vocabulary.
const typeTone: Record<string, PillTone> = {
  Engineer: 'info',
  Draftsperson: 'complete',
  Accounts: 'warning',
};
const roleTone: Record<string, PillTone> = {
  manager: 'success',
  qa: 'warning',
  basic: 'neutral',
};
const roleLabel: Record<string, string> = {
  manager: 'Manager',
  qa: 'QA (Auditor)',
  basic: 'Basic User',
};

/** "Nicos Terpkos" -> "NT"; a single word gives its first two letters. */
const initials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0]?.slice(0, 2).toUpperCase() ?? '';
};

const EmployeeTable: React.FC<EmployeeTableProps> = ({ employees, onRowClick }) => {
  // Column budget (table-layout: fixed; see the Documents register). Name is
  // the one `auto` column.
  const columns = useMemo<ColumnDef<Employee>[]>(() => [
    {
      id: 'name',
      header: 'Name',
      accessorFn: (row) => getEmployeeName(row),
      meta: { minWidth: '240px', width: 'auto' },
      cell: ({ row }) => {
        const name = getEmployeeName(row.original);
        return (
          <span className="employee-name" title={row.original.email ?? undefined}>
            <span className="ui-avatar" aria-hidden="true">{initials(name)}</span>
            {name}
          </span>
        );
      },
    },
    {
      accessorKey: 'type',
      header: 'Type',
      meta: { minWidth: '140px', width: '140px' },
      cell: ({ getValue }) => <StatusPill label={getValue<string>()} tone={typeTone[getValue<string>()] ?? 'neutral'} />,
    },
    {
      accessorKey: 'role',
      header: 'Role',
      meta: { minWidth: '140px', width: '140px' },
      cell: ({ getValue }) => {
        const role = getValue<string>();
        return <StatusPill label={roleLabel[role] ?? role} tone={roleTone[role] ?? 'neutral'} />;
      },
    },
    {
      id: 'status',
      header: 'Status',
      accessorFn: (row) => getEmployeeStatus(row),
      meta: { minWidth: '112px', width: '112px' },
      cell: ({ row }) =>
        row.original.is_active
          ? <StatusPill label="Active" tone="success" />
          : <StatusPill label="Inactive" tone="danger" />,
    },
    {
      id: 'charge_rate',
      header: 'Rate',
      accessorFn: (row) => row.current_charge_rate,
      meta: { minWidth: '136px', width: '136px', align: 'right' },
      cell: ({ row }) => {
        const rate = row.original.current_charge_rate;
        if (!rate) return <span className="text-muted">—</span>;
        return `$${Number(rate).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      },
    },
    {
      id: 'created_at',
      header: 'Joined',
      accessorKey: 'created_at',
      meta: { minWidth: '128px', width: '128px' },
      cell: ({ getValue }) => formatDate(getValue<string>()),
    },
  ], []);

  return <Table data={employees} columns={columns} onRowClick={onRowClick} stickyHeader />;
};

export default EmployeeTable;
